import "./SearchControl.scss";

import React from 'react';
import {FolderLocation} from '../../locations/FolderLocation';
import SearchIcon from '@mui/icons-material/Search';
import {SmallLoadingIndicator} from '../common/SmallLoadingIndicator';
import {useAppDispatch, useAppSelector} from "../../hooks/ReduxHooks";
import {PreferenceActions} from "../../redux/PreferencesSlice";
import {AccountsFolderId, AttachmentFolderId, NotesFolderId, PhotosFolderId, ProtectedFilesFolderId} from "../../reducers/FolderReducer";
import {CommonSearchControl} from "./Search/CommonSearchControl";
import {AccountsActions} from "../../redux/AccountsSlice";
import {FolderTypeEnum} from "../../domain/Folder";
import {ContactActions} from "../../redux/ContactSlice";
import {AttachmentFilesActions} from "../../redux/AttachmentFilesSlice";
import {ProtectedFilesActions} from "../../redux/ProtectedFilesSlice";
import {PhotosActions} from "../../redux/PhotosSlice";

export interface SearchControlProps {
    showWhenMobile: boolean;
}

export const SearchControl = ({
                                  showWhenMobile,
                              }: SearchControlProps) => {

    const dispatch = useAppDispatch();

    const showingSearch = useAppSelector(s => s.preferenceState.showingSearch);

    const searchText = useAppSelector(s => s.folderState.searchText);
    const hasNotesSearchText = useAppSelector(s => !!s.folderState.notesSearchText);
    const isMobile = useAppSelector(s => s.system.isMobile);
    const isOffline = useAppSelector(s => s.system.isOffline);

    const searchInProgress = useAppSelector(s => s.folderState.searchInProgress);
    const selectedFolderId = useAppSelector(s => s.folderState.selectedFolderId);
    const isContactsFolder = useAppSelector(s => s.folderState.selectedFolder?.type === FolderTypeEnum.Contacts);

    if (showWhenMobile !== isMobile) {
        return null;
    }

    if (isMobile && !searchText && !showingSearch && !hasNotesSearchText) {
        return (
            <span className={"selectable pointer"}>
                <SearchIcon fontSize="large" onClick={() => dispatch(PreferenceActions.changeShowingSearch(true))}/>
            </span>
        );
    }

    if (selectedFolderId === NotesFolderId) {
        return <CommonSearchControl promptText="Search Notes"
                                    searchTextSelector={state => state.folderState.notesSearchText}
                                    onSearchTextChange={text => FolderLocation.searchForNotesText(text)}/>;
    }
    if (selectedFolderId === AccountsFolderId) {
        return <CommonSearchControl promptText="Search Accounts"
                                    searchTextSelector={state => state.accountsState.accountSearchText}
                                    onSearchTextChange={text => dispatch(AccountsActions.setAccountSearchText(text))}/>;
    }
    if (selectedFolderId === AttachmentFolderId) {
        return <CommonSearchControl promptText="Search Attachments"
                                    disabled={isOffline}
                                    searchTextSelector={state => state.attachmentsState.searchText}
                                    onSearchTextChange={text => dispatch(AttachmentFilesActions.setAttachmentsSearchText(text))}/>;
    }
    if (selectedFolderId === ProtectedFilesFolderId) {
        return <CommonSearchControl promptText="Search Files"
                                    searchTextSelector={state => state.filesState.filterText}
                                    onSearchTextChange={text => dispatch(ProtectedFilesActions.setFilterText(text))}/>;
    }
    if (selectedFolderId === PhotosFolderId) {
        return <CommonSearchControl promptText="Search Photos"
                                    disabled={isOffline}
                                    searchTextSelector={state => state.photosState.searchText}
                                    onSearchTextChange={text => dispatch(PhotosActions.setPhotosSearchText(text))}/>;
    }
    if (isContactsFolder) {
        return <CommonSearchControl promptText="Search Contacts"
                                    searchTextSelector={state => state.contactState.contactSearchText}
                                    onSearchTextChange={text => dispatch(ContactActions.setContactSearchText(text))}/>;
    }
    return (<>
        <CommonSearchControl promptText="Search Emails"
                             disabled={isOffline}
                             searchTextSelector={state => state.folderState.searchText}
                             onSearchTextChange={text => FolderLocation.searchForText(text)}/>
        {searchInProgress ? <SmallLoadingIndicator className="search-indicator"/> : null}
    </>);
};
